import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X } from 'lucide-react';
import { Button } from '../../ui/Button';
import { uiSeatChip } from '../../ui/seatClasses';
import {
  CREW_SUMMARY_LABEL_TO_METRIC,
  CREW_SUMMARY_METRIC_ORDER,
  type ArcTargets,
  type CrewMetricRanks,
  type CrewSeatSummary,
  formatArcTargetsSummary,
  formatCoachHighlightLine,
  formatSeatMetricDegrees,
  getHitRatePercentStatus,
  type MetricType,
} from './arcMetrics';
import { getStatusTextClass } from './arcVisualSystem';

interface ArcCrewSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  summaries: CrewSeatSummary[];
  ranks: CrewMetricRanks;
  targets: ArcTargets;
  sessionLabel?: string;
  selectedSeat: number;
  onSelectSeat: (seat: number) => void;
  onFocusSeatMetric: (seat: number, metric: MetricType) => void;
}

const ArcCrewSummaryModal: React.FC<ArcCrewSummaryModalProps> = ({
  isOpen,
  onClose,
  summaries,
  ranks,
  targets,
  sessionLabel,
  selectedSeat,
  onSelectSeat,
  onFocusSeatMetric,
}) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const handlePrint = () => {
    window.print();
  };

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const printedAt = new Date().toLocaleString();

  return createPortal(
    <div
      className="ui-modal-overlay crew-summary-print-overlay z-[100]"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
      role="presentation"
    >
      <div
        className="ui-modal-panel max-w-5xl crew-summary-print-panel"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="crew-summary-title"
      >
        <div className="border-b border-border p-4 pt-6 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 id="crew-summary-title" className="text-xl font-semibold text-content-primary">
                Crew arc summary
              </h2>
              <p className="mt-1 text-sm text-content-secondary">
                {sessionLabel ? `${sessionLabel} · ` : ''}
                In-target % by seat — higher is better.
              </p>
              <p className="crew-summary-print-only mt-1 hidden text-xs text-content-muted">
                Printed {printedAt}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="crew-summary-print-hide rounded-full p-2 text-content-muted transition-colors hover:bg-surface-muted hover:text-content-secondary"
              aria-label="Close crew summary"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>

          <p className="mt-3 rounded-lg border border-border bg-surface-muted px-3 py-2 text-xs text-content-secondary">
            Targets: {formatArcTargetsSummary(targets)}
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-4 py-4 sm:px-6">
          <table className="ui-table w-full border border-border rounded-lg">
            <thead className="ui-table-head">
              <tr>
                <th className="ui-table-head-cell text-center">Seat</th>
                <th className="ui-table-head-cell">Work on</th>
                <th className="ui-table-head-cell">Keep up</th>
                {CREW_SUMMARY_METRIC_ORDER.map((label) => (
                  <th key={label} className="ui-table-head-cell text-center">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="ui-table-body">
              {summaries.map((summary) => {
                const isSelected = summary.seat === selectedSeat;
                const hasData = summary.strokeCount > 0;
                const workOn = summary.highlights?.weakest;
                const keepUp = summary.highlights?.strongest;

                return (
                  <tr
                    key={summary.seat}
                    className={`ui-table-row ${isSelected ? 'bg-accent/5' : ''}`}
                  >
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => {
                          onSelectSeat(summary.seat);
                          onClose();
                        }}
                        className={`crew-summary-print-hide ${uiSeatChip(isSelected ? 'selected' : 'default')} ${
                          !hasData ? 'opacity-50' : ''
                        }`}
                        aria-pressed={isSelected}
                        aria-label={`Select seat ${summary.seat}`}
                      >
                        {summary.seat}
                      </button>
                      <span className="crew-summary-print-only hidden font-semibold text-content-primary">
                        {summary.seat}
                      </span>
                      {!hasData && (
                        <span className="mt-1 block text-[11px] text-content-muted">
                          No strokes
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-content-primary">
                      {workOn ? formatCoachHighlightLine(workOn) : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-content-secondary">
                      {keepUp ? formatCoachHighlightLine(keepUp) : '—'}
                    </td>
                    {CREW_SUMMARY_METRIC_ORDER.map((label) => {
                      const value =
                        summary.hitRates.find((rate) => rate.label === label)?.value ?? null;
                      const metricSummary = summary.metricSummaries.find(
                        (entry) => entry.label === label
                      );
                      const status = getHitRatePercentStatus(value);
                      const rank = ranks.byMetric[label]?.get(summary.seat);

                      return (
                        <td key={label} className="px-4 py-3 text-center">
                          <button
                            type="button"
                            disabled={!hasData || value == null}
                            onClick={() => {
                              onFocusSeatMetric(summary.seat, CREW_SUMMARY_LABEL_TO_METRIC[label]);
                              onClose();
                            }}
                            className="crew-summary-print-hide mx-auto block text-center hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label={`Seat ${summary.seat}, ${label}: ${value == null ? 'no data' : `${Math.round(value)}% in target`}. Open in arc.`}
                          >
                            <span
                              className={`ui-table-cell-secondary text-sm font-semibold tabular-nums ${getStatusTextClass(status)}`}
                            >
                              {value == null ? '—' : `${Math.round(value)}%`}
                            </span>
                            {metricSummary && (
                              <span className="mt-0.5 block text-[10px] tabular-nums text-content-muted">
                                {formatSeatMetricDegrees(metricSummary)}
                              </span>
                            )}
                          </button>
                          <div className="crew-summary-print-only hidden text-center">
                            <span
                              className={`text-sm font-semibold tabular-nums ${getStatusTextClass(status)}`}
                            >
                              {value == null ? '—' : `${Math.round(value)}%`}
                            </span>
                            {metricSummary && (
                              <span className="mt-0.5 block text-[10px] tabular-nums text-content-muted">
                                {formatSeatMetricDegrees(metricSummary)}
                              </span>
                            )}
                          </div>
                          {rank != null && value != null && ranks.crewSize > 1 && (
                            <div className="text-[10px] tabular-nums text-content-muted">
                              {rank}/{ranks.crewSize}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="crew-summary-print-hide flex items-center justify-end gap-2 border-t border-border p-3 px-6">
          <Button variant="secondary" size="sm" onClick={handlePrint}>
            <Printer className="mr-1.5 h-4 w-4" aria-hidden />
            Print
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ArcCrewSummaryModal;
